import { useMemo } from 'react'
import useFlightDeals from 'hooks/Flights/useFlightDeals'
import { useOfferPageFlightPricePerPerson } from 'hooks/useFlightPrice'
import FlashHotelBundledFlightMerchandisingBanner from './FlashHotelBundledFlightMerchandisingBanner'

interface FlightDealFilters {
  originAirportCode?: string
  destinationAirportCode?: string
}

interface FallbackFlightPrice {
  fareType: 'return'
  perPersonPrice: number
}

interface FlightDealMerchandisingState {
  deals: Array<App.FlightDeal>
  fallbackFlightPrice?: FallbackFlightPrice
  showBanner: boolean
}

interface Props {
  flightOrigin?: App.Airport
  flightDestination?: App.Airport
  deals?: Array<App.FlightDeal>
  hasFlights?: boolean
  disabled?: boolean
  allowCalculatedFallback?: boolean
}

interface SlotContentProps extends Props {
  deals: Array<App.FlightDeal>
  flightDealsLoading?: boolean
}

function useFlightDealMerchandisingForFlashHotel(props: SlotContentProps): FlightDealMerchandisingState {
  const {
    flightOrigin,
    flightDestination,
    deals,
    flightDealsLoading = false,
    hasFlights,
    disabled,
    allowCalculatedFallback,
  } = props
  const { flightPricePerPerson, loading: flightPriceLoading } = useOfferPageFlightPricePerPerson({
    disabled: disabled || !allowCalculatedFallback || flightDealsLoading || deals.length > 0,
  })

  const fallbackFlightPrice = useMemo<FallbackFlightPrice | undefined>(() => {
    if (disabled || !allowCalculatedFallback || flightDealsLoading || flightPriceLoading || deals.length > 0 || !flightPricePerPerson) {
      return undefined
    }

    return {
      fareType: 'return',
      perPersonPrice: flightPricePerPerson,
    }
  }, [allowCalculatedFallback, deals.length, disabled, flightDealsLoading, flightPriceLoading, flightPricePerPerson])

  const canShowFlightDealMerchandisingBanner = !disabled &&
    !flightDealsLoading &&
    !!flightOrigin &&
    !!flightDestination &&
    (deals.length > 0 || (!!hasFlights && !!fallbackFlightPrice))

  return {
    deals,
    fallbackFlightPrice,
    showBanner: canShowFlightDealMerchandisingBanner,
  }
}

function FlashHotelFlightDealMerchandisingSlotContent(props: SlotContentProps) {
  const {
    deals,
    fallbackFlightPrice,
    showBanner,
  } = useFlightDealMerchandisingForFlashHotel(props)

  if (!showBanner) {
    return null
  }

  return (
    <FlashHotelBundledFlightMerchandisingBanner
      deals={deals}
      fallbackFlightPrice={fallbackFlightPrice}
    />
  )
}

function FlashHotelFlightDealMerchandisingSlotConnected(props: Props) {
  const {
    flightOrigin,
    flightDestination,
  } = props

  const flightDealFilters = useMemo<FlightDealFilters>(() => ({
    originAirportCode: flightOrigin?.code,
    destinationAirportCode: flightDestination?.code,
  }), [flightDestination?.code, flightOrigin?.code])

  const [deals, flightDealsLoading] = useFlightDeals('all', flightDealFilters)

  return (
    <FlashHotelFlightDealMerchandisingSlotContent
      flightOrigin={props.flightOrigin}
      flightDestination={props.flightDestination}
      deals={deals}
      flightDealsLoading={flightDealsLoading}
      hasFlights={props.hasFlights}
      disabled={props.disabled}
      allowCalculatedFallback={props.allowCalculatedFallback}
    />
  )
}

function FlashHotelFlightDealMerchandisingSlot(props: Props) {
  if (props.disabled || !props.flightOrigin || !props.flightDestination) {
    return null
  }

  if (props.deals !== undefined) {
    return (
      <FlashHotelFlightDealMerchandisingSlotContent
        flightOrigin={props.flightOrigin}
        flightDestination={props.flightDestination}
        deals={props.deals}
        hasFlights={props.hasFlights}
        disabled={props.disabled}
        allowCalculatedFallback={props.allowCalculatedFallback}
      />
    )
  }

  return (
    <FlashHotelFlightDealMerchandisingSlotConnected
      flightOrigin={props.flightOrigin}
      flightDestination={props.flightDestination}
      hasFlights={props.hasFlights}
      disabled={props.disabled}
      allowCalculatedFallback={props.allowCalculatedFallback}
    />
  )
}

export default FlashHotelFlightDealMerchandisingSlot
